import { useEffect, useState } from 'react'
import { useLDClient } from 'launchdarkly-react-client-sdk'

import useFeatureFlags from 'hooks/useFeatureFlags'
import useGlobalFlag from 'hooks/useGlobalFlag'

import { US_RESTRICTED_DATA_ENFORCEMENT_FLAG } from 'enrollment-platform/pages/finance/components/PaymentBotDashboard/shared/constants'

import { can } from './ability'
import { RbacSubjects, type RbacAction, type RbacSubject } from './constants'
import { RBAC_ENFORCEMENT_FLAG_KEY } from './enforcement-flag'
import { ldReadyState } from './_ldReadyState'
import { useMyAbilityQuery } from './useMyAbilityQuery'

// Maximum time to wait for the LD React SDK to initialize before failing open.
// Matches the LD JS SDK's own internal initialization timeout so we never
// wait longer than the SDK already would before giving up.
const LD_INIT_TIMEOUT_MS = 5_000

/**
 * Checks whether the current user can perform `action` on `subject`, based on
 * the CASL-style rules from GET /access-control/me/ability.
 *
 * `isLoading` lets callers avoid flashing an "access denied" state before the
 * ability has been fetched.
 *
 * Kill-switch: when the `admin.rbac-enforcement-enabled` LD flag is OFF (or LD
 * unavailable — the hook defaults it to false), enforcement is disabled and
 * every check returns `allowed: true` (with `isLoading: false`), so the React
 * gates (AdminPortalGate, RequirePermission) show everything and fall back to
 * the coarse role checks — matching the EPS server kill-switch.
 *
 * `UsRestrictedData` also follows the EPS global flag
 * `service.eps.us-restricted-data.enable-enforcement`, and only after that
 * flag has resolved. While it is off, the check is allowed. While it is on,
 * the LaunchDarkly kill-switch and ability rules below apply.
 *
 * LD readiness: the app uses `withLDProvider` (synchronous), so the React SDK
 * client renders the component tree immediately with default flag values while
 * its own async initialization is still in-flight. During that window,
 * `admin.rbac-enforcement-enabled` reads as `false` (the fail-safe default)
 * even when the real flag value is `true`. Without a readiness guard, gated UI
 * elements (tiles, links) would flash visible until the SDK fires `ready` and
 * `useFlags()` re-renders with the real values.
 *
 * To close that window, `usePermission` tracks LD readiness. While the SDK is
 * still initializing, it returns `{ allowed: false, isLoading: true }` so
 * callers keep the gate closed. Once the SDK fires `ready` (or fails — in which
 * case enforcement falls back to off, matching the fail-safe default), the real
 * flag value drives the check.
 *
 * Note: `useLDClient()` returns `undefined` while `withLDProvider` is still
 * creating the client. Only once it transitions to the actual `LDClient` can we
 * subscribe to `waitForInitialization()`. Treating `undefined` as "ready" would
 * bypass the guard entirely, so we wait for a non-null client before subscribing.
 *
 * @example
 *   const { allowed, isLoading } = usePermission('Manage', 'PermissionManagement')
 */
export function usePermission(action: RbacAction, subject: RbacSubject) {
  const isUsRestricted = subject === RbacSubjects.UsRestrictedData
  const { isEnabled: isEnforced, isLoading: isFlagLoading } = useGlobalFlag(
    US_RESTRICTED_DATA_ENFORCEMENT_FLAG
  )
  const { flags } = useFeatureFlags()
  const ldClient = useLDClient()

  // Lazy initializer: if LD already fired `ready` during a previous render or
  // in a sibling component, start ready immediately so re-mounts don't hide
  // content for one frame unnecessarily.
  const [isLdReady, setIsLdReady] = useState(() => ldReadyState.isReady)

  useEffect(() => {
    if (ldReadyState.isReady) return

    let cancelled = false

    // Fail-open helper — mirrors isRbacEnforcementEnabled()'s catch branch:
    // if LD never initializes, enforcement falls back to off (the kill-switch
    // default) so callers degrade to coarse role checks rather than staying
    // blank indefinitely.
    const unblock = () => {
      ldReadyState.isReady = true
      if (!cancelled) setIsLdReady(true)
    }

    // Safety net: fires if the client never appears (useLDClient() stays
    // undefined) OR if waitForInitialization() never settles. Either way,
    // after LD_INIT_TIMEOUT_MS we fail-open so the UI is never permanently
    // hidden. The timeout resets each time ldClient changes (effect re-runs),
    // so the window is measured from when the client first becomes available.
    const timeoutId = setTimeout(unblock, LD_INIT_TIMEOUT_MS)

    if (ldClient) {
      // Race the SDK's own initialization against the safety-net timeout.
      // Both resolve to unblock(), so whichever fires first wins.
      ldClient.waitForInitialization().then(unblock, unblock)
    }
    // If ldClient is still undefined here, withLDProvider hasn't published the
    // client yet. The timeout above is the only active safety net. When ldClient
    // becomes non-null, this effect re-runs (ldClient is a dep), cancels the
    // current timeout, and starts a fresh race with the new client.

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [ldClient])

  const { data, isLoading, isError } = useMyAbilityQuery()

  // LD React SDK hasn't initialized yet — can't reliably read the enforcement
  // flag. Keep the gate closed (isLoading:true) to prevent gated content from
  // flashing visible during the init window.
  if (!isLdReady) {
    return { allowed: false, isLoading: true, isError: false }
  }

  if (isUsRestricted && !isFlagLoading && !isEnforced) {
    return { allowed: true, isLoading: false, isError: false }
  }

  if (!flags[RBAC_ENFORCEMENT_FLAG_KEY]) {
    return { allowed: true, isLoading: false, isError: false }
  }

  return {
    allowed: can(data?.rules, action, subject),
    isLoading: isLoading || (isUsRestricted && isFlagLoading),
    isError,
  }
}
